import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { GuestUsageProvider } from "./contexts/GuestUsageContext";
import { supabase } from "./integrations/supabase/client";
import BackgroundGlow from "./components/BackgroundGlow";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import FeedbackPrompt from "./components/feedback/FeedbackPrompt";
import AnimatedLogoLoader from "./components/AnimatedLogoLoader";
import AuthLoadingScreen from "./components/AuthLoadingScreen";

// Lazy load components to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SpotifyCallback = lazy(() => import("./pages/SpotifyCallback"));
const SpotifyPage = lazy(() => import("./pages/SpotifyPage"));

// Custom suspense fallback that uses the AnimatedLogoLoader
const SuspenseFallback = () => <AnimatedLogoLoader isLoading={true} minDisplayTime={800} />;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Helper component to handle OAuth redirects
const AuthRedirect = () => {
  const [shouldRedirect, setShouldRedirect] = useState(false);
  
  useEffect(() => {
    const { hash } = window.location;
    if (hash && hash.includes("access_token")) {
      // Handle the OAuth callback
      supabase.auth.getSession();
      
      // Delay redirect to allow loading screen to show
      setTimeout(() => {
        setShouldRedirect(true);
      }, 150);
    } else {
      // No hash, can redirect immediately
      setShouldRedirect(true);
    }
  }, []);

  return shouldRedirect ? <Navigate to="/" /> : null;
};

// Component to handle scrolling to sections
export const ScrollToSection = ({ sectionId }: { sectionId: string }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Wait a longer time to ensure the page is fully loaded
    const timer = setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Get the navbar height to offset the scroll position
        const navbarHeight = 100; // Increased offset to fix the spacing issue
        
        // Calculate the element's position relative to the top of the page
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - navbarHeight;
        
        // Scroll to the element with the adjusted offset
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
      
      // Update URL without full page reload
      navigate('/', { replace: true });
    }, 300); // Increased timeout to ensure everything is loaded
    
    return () => clearTimeout(timer);
  }, [sectionId, navigate]);
  
  return null;
};

// Wrap Spotify route to check for login
const ProtectedSpotifyRoute = () => {
  const { user } = useAuth();
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(!user);

  useEffect(() => {
    setIsLoginPromptOpen(!user);
  }, [user]);

  if (!user) {
    return (
      <div>
        <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Sign In Required</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>Please sign in to connect to Spotify. This feature requires an account.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => window.location.href = "/"}>
                Go Back
              </Button>
              <Button onClick={() => window.location.href = "/auth"}>
                Sign In
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Navigate to="/" />
      </div>
    );
  }

  return <SpotifyPage />;
};

// AppContent wrapper component that includes the AuthLoadingScreen
const AppContent = () => {
  return (
    <>
      <AuthLoadingScreen minLoadTime={1800} />
      <Toaster />
      <Sonner theme="dark" />
      <BackgroundGlow />
      <FeedbackPrompt />
      <BrowserRouter basename="/">
        <Suspense fallback={<SuspenseFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/spotify" element={<ProtectedSpotifyRoute />} />
            <Route path="/spotify/callback" element={<SpotifyCallback />} />
            <Route path="/auth/callback" element={<AuthRedirect />} />
            {/* Routes to handle OAuth redirects */}
            <Route path="/auth/github" element={<Navigate to="/auth" />} />
            <Route path="/auth/google" element={<Navigate to="/auth" />} />
            {/* Routes for scrolling to specific sections */}
            <Route path="/components" element={<ScrollToSection sectionId="component-library" />} />
            <Route path="/canvas" element={<ScrollToSection sectionId="canvas" />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <GuestUsageProvider>
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </GuestUsageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
